// API layer for the Grapevine Jobs channel.
//
// apiClient still attaches the Firebase ID token. The jobs host is
// VITE_GRAPEVINE_API_URL so a local/dev LIFT Admin can talk to a different
// grapevine-api instance than VITE_BASEURL. Axios treats an absolute URL as
// replacing the client base, so the token interceptor still applies.
//
// The istio gateway on that host checks the caller's grapevine role before
// the request reaches Rails, so a 403 here means the user's IAM level is
// below what the endpoint requires.

import apiClient from '@lift-admin/utils/apiClient';
import type {
  ActionResult,
  BulkResult,
  CreateSyncRunsRequest,
  CreateSyncRunsResult,
  JobDetail,
  JobSummary,
  JobsFilters,
  Overview,
  Paginated,
  Queue,
  RecurringTask,
  ResolveSyncEmailsResult,
  SyncRunDetail,
  SyncRunItemQuery,
  SyncRunSummary,
  SyncRunZip,
  SyncUsersQuery,
  SyncUsersResponse,
  Worker,
} from './types';

const grapevineApiOrigin = () => {
  const explicit = (process.env.VITE_GRAPEVINE_API_URL || '').replace(/\/$/, '');
  if (explicit) return explicit;

  const liftBase = (process.env.VITE_BASEURL || '').replace(/\/$/, '');
  return `${liftBase}/grapevine-api`;
};

const BASE = `${grapevineApiOrigin()}/api/v1/admin/jobs`;

/** Message from the Rails error body, falling back to something actionable. */
export function jobsErrorMessage(error: unknown, fallback: string): string {
  const response = (error as { response?: { status?: number; data?: { message?: string } } })
    ?.response;

  if (response?.status === 403) {
    return 'You do not have permission to perform this action on Grapevine jobs.';
  }

  return response?.data?.message ?? fallback;
}

const params = (filters: object) =>
  Object.fromEntries(
    Object.entries(filters).filter(
      ([, value]) => value !== undefined && value !== null && value !== '' && value !== 'all'
    )
  );

export async function getOverview(): Promise<Overview> {
  const { data } = await apiClient.get<{ data: Overview }>(`${BASE}/overview`);
  return data.data;
}

export async function getJobs(filters: JobsFilters): Promise<Paginated<JobSummary>> {
  const { data } = await apiClient.get<Paginated<JobSummary>>(BASE, { params: params(filters) });
  return data;
}

export async function getJob(jobId: string): Promise<JobDetail> {
  const { data } = await apiClient.get<{ data: JobDetail }>(`${BASE}/${jobId}`);
  return data.data;
}

export async function getQueues(): Promise<Queue[]> {
  const { data } = await apiClient.get<{ data: Queue[] }>(`${BASE}/queues`);
  return data.data;
}

export async function getWorkers(page = 1, pageSize = 25): Promise<Paginated<Worker>> {
  const { data } = await apiClient.get<Paginated<Worker>>(`${BASE}/workers`, {
    params: { page, page_size: pageSize },
  });
  return data;
}

export async function getSyncRuns(page = 1): Promise<Paginated<SyncRunSummary>> {
  const { data } = await apiClient.get<Paginated<SyncRunSummary>>(`${BASE}/sync-runs`, {
    params: { page, page_size: 25 },
  });
  return data;
}

export async function getSyncRun(id: string, query: SyncRunItemQuery = {}): Promise<SyncRunDetail> {
  const { data } = await apiClient.get<{ data: SyncRunDetail }>(`${BASE}/sync-runs/${id}`, {
    params: {
      page: query.page ?? 1,
      page_size: query.pageSize ?? 25,
      status: query.status || undefined,
      q: query.q || undefined,
    },
  });
  return data.data;
}

export async function getSyncUsers(query: SyncUsersQuery = {}): Promise<SyncUsersResponse> {
  const { data } = await apiClient.get<SyncUsersResponse>(`${BASE}/sync-users`, {
    params: params({
      type: query.type,
      q: query.q?.trim(),
      page: query.page ?? 1,
      page_size: query.pageSize ?? 25,
    }),
  });
  return data;
}

export async function resolveSyncEmails(emails: string[]): Promise<ResolveSyncEmailsResult> {
  const { data } = await apiClient.post<ResolveSyncEmailsResult>(`${BASE}/sync-users/resolve`, {
    emails,
  });
  return data;
}

export async function getSyncRunZip(id: string): Promise<SyncRunZip> {
  const { data } = await apiClient.get<SyncRunZip>(`${BASE}/sync-runs/${id}/zip`);
  return data;
}

export async function getRecurringTasks(): Promise<RecurringTask[]> {
  const { data } = await apiClient.get<{ data: RecurringTask[] }>(`${BASE}/recurring_tasks`);
  return data.data;
}

// Editor
export async function retryJob(jobId: string): Promise<ActionResult<JobSummary>> {
  const { data } = await apiClient.post<ActionResult<JobSummary>>(`${BASE}/${jobId}/retry`);
  return data;
}

export async function dispatchJob(jobId: string): Promise<ActionResult<JobSummary>> {
  const { data } = await apiClient.post<ActionResult<JobSummary>>(`${BASE}/${jobId}/dispatch`);
  return data;
}

export async function retryAll(
  filters: Pick<JobsFilters, 'job_class_name' | 'queue_name'>
): Promise<ActionResult<BulkResult>> {
  const { data } = await apiClient.post<ActionResult<BulkResult>>(
    `${BASE}/bulk_retries`,
    params(filters)
  );
  return data;
}

export async function runRecurringTask(taskId: string): Promise<ActionResult<{ job_id: string }>> {
  const { data } = await apiClient.post<ActionResult<{ job_id: string }>>(
    `${BASE}/recurring_tasks/${taskId}/run`
  );
  return data;
}

export async function createSyncRuns(request: CreateSyncRunsRequest): Promise<CreateSyncRunsResult> {
  const { data } = await apiClient.post<CreateSyncRunsResult>(`${BASE}/sync-runs`, request);
  return data;
}

// Manager
export async function discardJob(jobId: string): Promise<ActionResult<JobSummary>> {
  const { data } = await apiClient.post<ActionResult<JobSummary>>(`${BASE}/${jobId}/discard`);
  return data;
}

export async function discardAll(
  filters: Pick<JobsFilters, 'job_class_name' | 'queue_name'>
): Promise<ActionResult<BulkResult>> {
  const { data } = await apiClient.post<ActionResult<BulkResult>>(
    `${BASE}/bulk_discards`,
    params(filters)
  );
  return data;
}

export async function pauseQueue(queueId: string): Promise<ActionResult<Queue>> {
  const { data } = await apiClient.post<ActionResult<Queue>>(`${BASE}/queues/${queueId}/pause`);
  return data;
}

export async function resumeQueue(queueId: string): Promise<ActionResult<Queue>> {
  const { data } = await apiClient.delete<ActionResult<Queue>>(`${BASE}/queues/${queueId}/pause`);
  return data;
}
